import React, { useEffect, useMemo, useState } from "react";
import { Col, Row, Table, Button } from "reactstrap";
import { AiFillDelete, AiFillEdit, AiOutlineSearch } from "react-icons/ai";
import { Modal, Input, Select } from "antd";

import SideBar from "../../components/SideBar";
import Header from "../../components/DataManagement/Float/Header";

import {
  getAllVehicul,
  updateVehiculStatus,
  updateVehicul,
  deleteOneVehicul,
  listOfMatricul,
  listOfResponsable,
} from "./../../api/data/Vehiculs";

import Swal from "sweetalert2";

const { Option } = Select;

function formatDate(value) {
  if (!value) return "—";
  const d = new Date(value);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleDateString("fr-FR", { day: "2-digit", month: "2-digit", year: "numeric" });
}

function FloatTable() {
  const [FlottData, setFlottData] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);
  const [searchTerm, setSearchTerm] = useState("");

  const [editModal, setEditModal] = useState(false);
  const [editData, setEditData] = useState(null);
  const [RHNames, setRHNames] = useState([]);
  const [MatriculNames, setMatriculNames] = useState([]);

  const getFlotteData = async () => {
    try {
      setIsLoading(true);
      setHasError(false);

      const response = await getAllVehicul();

      if (response && !Array.isArray(response)) {
        setHasError(true);
        setFlottData([]);
        return;
      }

      setFlottData(Array.isArray(response) ? response : []);
    } catch (err) {
      setHasError(true);
      setFlottData([]);
    } finally {
      setIsLoading(false);
    }
  };

  const getSelectData = async () => {
    try {
      const [matricul, rh] = await Promise.all([listOfMatricul(), listOfResponsable()]);
      setMatriculNames(Array.isArray(matricul) ? matricul : []);
      setRHNames(Array.isArray(rh) ? rh : []);
    } catch (err) {
      setMatriculNames([]);
      setRHNames([]);
    }
  };

  const filteredData = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return FlottData;

    return FlottData.filter(el => {
      const responsableName = el.responsable ? `${el.responsable.firstName} ${el.responsable.lastName}` : "";
      const immatriculation = el.immatriculation?.immatriculation || "";

      return [el.marque, el.modele, immatriculation, responsableName]
        .filter(Boolean)
        .some(field => field.toLowerCase().includes(term));
    });
  }, [FlottData, searchTerm]);

  const handleEditClick = (el) => {
    setEditData({
      _id: el._id,
      marque: el.marque,
      modele: el.modele,
      miseEnCirculation: el.miseEnCirculation ? el.miseEnCirculation.substr(0, 10) : "",
      immatriculation: el.immatriculation?._id || el.immatriculation || "",
      responsable: el.responsable?._id || el.responsable || "",
      cartGris: el.cartGris,
      prefectorale: Array.isArray(el.prefectorale) ? el.prefectorale : [],
    });
    setEditModal(true);
  };

  const handleEditChange = (e) => {
    const { name, value } = e.target;
    setEditData({ ...editData, [name]: value });
  };

  const handleUpdateSubmit = async () => {
    try {
      const { _id, ...data } = editData;
      const response = await updateVehicul(_id, data);
      if (response) {
        setEditModal(false);
        getFlotteData();
        Swal.fire("Modifié", "", "success");
      }
    } catch (err) {
      Swal.fire("Erreur", err?.response?.data?.error || "", "error");
    }
  };

  const handleStateChange = async (id, status) => {
    try {
      Swal.fire({
        title:
          status === "V"
            ? "Voulez-vous valider cette opération ?"
            : "Voulez-vous annuler cette opération ?",
        icon: "warning",
        confirmButtonColor: status === "V" ? "#198754" : "#dc3545",
        confirmButtonText: status === "V" ? "Valider" : "Annuler",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await updateVehiculStatus(id, status);
            if (response) {
              getFlotteData();
              Swal.fire(status === "V" ? "Validée" : "Annulée", "", "success");
            }
          } catch (err) {
            Swal.fire("Erreur", "", "error");
          }
        }
      });
    } catch (err) {}
  };

  const handleDelete = async (id) => {
    try {
      Swal.fire({
        title: "Voulez-vous supprimer ce véhicule ?",
        icon: "warning",
        confirmButtonColor: "#dc3545",
        confirmButtonText: "Supprimer",
      }).then(async (result) => {
        if (result.isConfirmed) {
          try {
            const response = await deleteOneVehicul(id);
            if (response) {
              getFlotteData();
              Swal.fire("Supprimée", "", "success");
            }
          } catch (err) {
            Swal.fire("Erreur", "", "error");
          }
        }
      });
    } catch (err) {}
  };

  useEffect(() => {
    getFlotteData();
    getSelectData();
  }, []);

  return (
    <section className="floatTableSection">
      <div className="contents">
        <Row>
          <Col md="2" className="p-0">
            <SideBar />
          </Col>
          <Col md="10" className="p-0">
            <Header />

            <div className="tableSection">
              <div className="tableToolbar">
                <Input
                  prefix={<AiOutlineSearch />}
                  placeholder="Rechercher un véhicule, une marque, un responsable…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  allowClear
                />
              </div>

              <div className="table-wrapper">
                {isLoading && (
                  <p className="stateMessage">Chargement de la flotte…</p>
                )}

                {!isLoading && hasError && (
                  <p className="stateMessage error">Impossible de charger la flotte. Réessayez dans un instant.</p>
                )}

                {!isLoading && !hasError && FlottData.length === 0 && (
                  <p className="stateMessage">Aucun véhicule enregistré. Cliquez sur « Ajouter une véhicule » pour commencer.</p>
                )}

                {!isLoading && !hasError && FlottData.length > 0 && filteredData.length === 0 && (
                  <p className="stateMessage">Aucun véhicule ne correspond à « {searchTerm} ».</p>
                )}

                {!isLoading && !hasError && filteredData.length > 0 && (
                  <Table striped>
                    <thead>
                      <tr>
                        <th>Marque</th>
                        <th>Modèle</th>
                        <th>Date</th>
                        <th>Immatriculation</th>
                        <th>Responsable</th>
                        <th>Carte grise</th>
                        <th>Préfectorale</th>
                        <th>Etat d'opération</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {filteredData.map((el) => (
                        <tr key={el._id}>
                          <td>{el.marque}</td>
                          <td>{el.modele}</td>
                          <td>{formatDate(el.miseEnCirculation)}</td>
                          <td>{el.immatriculation ? el.immatriculation.immatriculation : "-"}</td>
                          <td>{el.responsable ? `${el.responsable.firstName} ${el.responsable.lastName}` : "-"}</td>
                          <td>{el.cartGris}</td>
                          <td>
                            {Array.isArray(el.prefectorale) && el.prefectorale.length > 0 ? (
                              <div className="tagList">
                                {el.prefectorale.map((tag, i) => (
                                  <span className="tagPill" key={i}>{tag}</span>
                                ))}
                              </div>
                            ) : "-"}
                          </td>
                          <td>
                            {el.status === "N" && (
                              <div className="btnStatus">
                                <Button size="sm" color="success" onClick={() => handleStateChange(el._id, "V")}>Valider</Button>
                                <Button size="sm" color="danger" onClick={() => handleStateChange(el._id, "A")}>Annuler</Button>
                              </div>
                            )}
                            {el.status === "V" && <span className="status-badge badge-valid">✓ Validée</span>}
                            {el.status === "A" && <span className="status-badge badge-canceled">✕ Annulée</span>}
                          </td>
                          <td>
                            <div className="action-icons">
                              <button className="action-btn edit-btn" onClick={() => handleEditClick(el)}><AiFillEdit size="1rem" /></button>
                              <button className="action-btn delete-btn" onClick={() => handleDelete(el._id)}><AiFillDelete size="1rem" /></button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </Table>
                )}
              </div>
            </div>
          </Col>
        </Row>
      </div>

      <Modal
        title="Modifier le véhicule"
        open={editModal}
        onOk={handleUpdateSubmit}
        onCancel={() => setEditModal(false)}
        okText="Enregistrer"
        cancelText="Annuler"
      >
        {editData && (
          <div style={{ display: "flex", flexDirection: "column", gap: "10px" }}>
            <Input placeholder="Marque" name="marque" value={editData.marque} onChange={handleEditChange} />
            <Input placeholder="Modèle" name="modele" value={editData.modele} onChange={handleEditChange} />
            <Input placeholder="Date mise en circulation (YYYY-MM-DD)" name="miseEnCirculation" value={editData.miseEnCirculation} onChange={handleEditChange} />

            <Select
              placeholder="Responsable"
              value={editData.responsable || undefined}
              onChange={(val) => setEditData({ ...editData, responsable: val })}
            >
              {RHNames.map((el) => (
                <Option key={el._id} value={el._id}>
                  {el.firstName} {el.lastName}
                </Option>
              ))}
            </Select>

            <Select
              placeholder="Immatriculation"
              value={editData.immatriculation || undefined}
              onChange={(val) => setEditData({ ...editData, immatriculation: val })}
            >
              {MatriculNames.map((el) => (
                <Option key={el._id} value={el._id}>
                  {el.immatriculation}
                </Option>
              ))}
            </Select>

            <Input placeholder="Carte grise" name="cartGris" value={editData.cartGris} onChange={handleEditChange} />

            <Select
              mode="tags"
              style={{ width: "100%" }}
              placeholder="Affectation préfectorale"
              value={editData.prefectorale}
              onChange={(values) => setEditData({ ...editData, prefectorale: values })}
            />
          </div>
        )}
      </Modal>
    </section>
  );
}

export default FloatTable;
